import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"
import { AlertCircle, MessageSquare, SlidersHorizontal } from "lucide-react"

import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { PageNav, ResizeHandle } from "@/components"
import { OnboardingEmptyState, LoadingPage } from "@/components/System"
import { useToast, useDrawerResize, useWindowSize } from "@/hooks"
import {
  useGetHistoriesApi,
  historyResponseToHistory,
  useGetAgentsApi,
  agentResponseToAgent,
} from "@/hooks/api"
import { FetchStatus } from "@/hooks/useFetch"
import { HTTPError, TimeoutError } from "ky"
import { History, Workspace, Agent } from "@/types"
import { DEFAULT_LOADING_MESSAGE } from "@/constants"
import { useStore } from "@/store"
import {
  HistoryDetail,
  HISTORY_DETAIL_SCROLL_CONTAINER,
} from "@/components/History"

import {
  HistoryCard,
  WorkspaceSelect,
  AgentSelect,
  ExternalUserIdSelect,
  SortToggle,
  FeedbackSelect,
  FeedbackFilterValue,
  HumanModeSelect,
  HumanModeFilterValue,
  SourceSelect,
  SourceFilterValue,
  CHANNEL_SOURCE_FILTER_VALUES,
} from "./components"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { CustomerSearch } from "../CustomerSearch"

type HistoryOrderBy = "asc" | "desc"

const RESETTABLE_FILTER_PARAM_KEYS = [
  "aid",
  "pem",
  "cuid",
  "customer",
  "fb",
  "hitl",
  "ch",
  "src",
  "ob",
] as const
const AGENT_FILTER_ALL = "all"
const AGENT_FILTER_NONE = "none"
const DEFAULT_HISTORY_ORDER: HistoryOrderBy = "desc"
const DEFAULT_FEEDBACK_FILTER: FeedbackFilterValue = "no_filter"
const DEFAULT_HUMAN_MODE_FILTER: HumanModeFilterValue = "no_filter"
const DEFAULT_SOURCE_FILTER: SourceFilterValue = "all"
const HISTORY_FOCUS_PARAM_VALUE = "history"
const CHANNEL_SOURCE_FILTER_SET = new Set<string>(CHANNEL_SOURCE_FILTER_VALUES)
const HISTORY_SOURCE_FILTER_SET = new Set<string>([
  DEFAULT_SOURCE_FILTER,
  "workspace_api_key",
  "client_channel",
  ...CHANNEL_SOURCE_FILTER_VALUES,
])
export const HISTORY_DETAIL_WIDTH_STORAGE_KEY = "historyList.detailPaneWidth"
const HISTORY_DETAIL_MIN_WIDTH = 420
const HISTORY_DETAIL_COMPACT_MIN_WIDTH = 320
const HISTORY_DETAIL_MAX_WIDTH = 920
const HISTORY_LIST_MIN_WIDTH = 360
const HISTORY_DETAIL_DEFAULT_WIDTH = 680
// 1px border on each side of the split divider.
const SPLIT_PANE_BORDER_PX = 2

const clampHistoryDetailWidth = (
  value: number,
  maxWidth: number,
  minWidth = HISTORY_DETAIL_MIN_WIDTH
) => Math.max(minWidth, Math.min(maxWidth, value))

const getInitialHistoryDetailWidth = () => {
  if (typeof window === "undefined") return HISTORY_DETAIL_DEFAULT_WIDTH

  const storedWidth = Number.parseInt(
    window.localStorage.getItem(HISTORY_DETAIL_WIDTH_STORAGE_KEY) || "",
    10
  )

  return Number.isNaN(storedWidth) ? HISTORY_DETAIL_DEFAULT_WIDTH : storedWidth
}

export const hasActiveHistoryFilters = (
  searchParams: URLSearchParams,
  workspace?: Workspace
) => {
  const aid = searchParams.get("aid")
  const feedbackFilter = searchParams.get("fb")
  const humanModeFilter = searchParams.get("hitl")
  const sourceFilter = searchParams.get("src") || searchParams.get("ch")
  return (
    (!!searchParams.get("wid") && !workspace) ||
    (aid !== null && aid !== AGENT_FILTER_ALL && aid !== AGENT_FILTER_NONE) ||
    !!searchParams.get("pem") ||
    !!searchParams.get("cuid") ||
    !!searchParams.get("customer")?.trim() ||
    (feedbackFilter !== null && feedbackFilter !== DEFAULT_FEEDBACK_FILTER) ||
    (humanModeFilter !== null &&
      humanModeFilter !== DEFAULT_HUMAN_MODE_FILTER) ||
    (sourceFilter !== null && sourceFilter !== DEFAULT_SOURCE_FILTER) ||
    searchParams.get("ob") === "asc"
  )
}

export const shouldShowHistoryOnboarding = ({
  historiesStatus,
  histories,
  hasActiveFilters,
}: {
  historiesStatus: FetchStatus
  histories?: History[]
  hasActiveFilters: boolean
}) =>
  historiesStatus === FetchStatus.Success &&
  histories !== undefined &&
  histories.length === 0 &&
  !hasActiveFilters

export const syncWorkspaceScopedHistoryParams = (
  searchParams: URLSearchParams,
  workspaceId: string
) => {
  if (searchParams.get("wid") === workspaceId) return null

  const params = new URLSearchParams(searchParams.toString())
  params.set("page", "1")
  params.set("wid", workspaceId)
  params.delete("pem")
  params.delete("cuid")
  params.delete("customer")
  params.delete("focus")
  return params
}

export const shouldDeferWorkspaceScopedHistoryFetch = (
  searchParams: URLSearchParams,
  workspace?: Workspace
) => !!workspace && searchParams.get("wid") !== workspace.id

export const buildHistoryResponseScopeKey = (
  searchParams: URLSearchParams,
  identityMasked: boolean
) => `${identityMasked ? "masked" : "unmasked"}:${searchParams.toString()}`

export const shouldApplyHistoryResponse = (
  requestGeneration: number,
  latestRequestGeneration: number,
  responseScopeKey: string,
  currentScopeKey: string
) =>
  requestGeneration === latestRequestGeneration &&
  responseScopeKey === currentScopeKey

export const selectHistoriesForScope = <T,>(
  loadedScopeKey: string,
  currentScopeKey: string,
  histories: T
): T | undefined => (loadedScopeKey === currentScopeKey ? histories : undefined)

export const changeHistoryWorkspaceParams = (
  searchParams: URLSearchParams,
  workspaceId: string
) => {
  const params = new URLSearchParams(searchParams.toString())
  params.set("page", "1")
  if (workspaceId === AGENT_FILTER_ALL) params.delete("wid")
  else params.set("wid", workspaceId)
  params.delete("pem")
  params.delete("cuid")
  params.delete("customer")
  params.delete("focus")
  return params
}

export const sanitizeMaskedHistoryIdentityParams = (
  searchParams: URLSearchParams,
  isMasked: boolean
) => {
  const externalUserId = searchParams.get("pem")
  if (
    !isMasked ||
    ((!externalUserId || externalUserId === "all") &&
      !searchParams.has("customer"))
  )
    return null

  const params = new URLSearchParams(searchParams.toString())
  params.set("page", "1")
  if (externalUserId !== "all") params.delete("pem")
  params.delete("customer")
  return params
}

export const isHistoryIdentityScopeMasked = (
  workspaces: Workspace[] | undefined,
  workspaceId: string
) => {
  // Fail closed until the current scope is known. Sending a raw identity
  // filter for an unknown or mixed scope would be rejected by the backend.
  if (!workspaces) return true
  if (!workspaceId) {
    return workspaces.some((workspace) => workspace.maskEndUserIdentity)
  }

  const selectedWorkspace = workspaces.find(
    (workspace) => workspace.id === workspaceId
  )
  return selectedWorkspace ? !!selectedWorkspace.maskEndUserIdentity : true
}

export const getSelectedHistoryId = (searchParams: URLSearchParams) => {
  const rawHistoryId = searchParams.get("hid")
  if (!rawHistoryId) return null

  const parsedHistoryId = Number.parseInt(rawHistoryId, 10)
  return Number.isNaN(parsedHistoryId) ? null : parsedHistoryId
}

export const isHistorySourceFilterValue = (
  value: string | null
): value is SourceFilterValue =>
  value !== null && HISTORY_SOURCE_FILTER_SET.has(value)

export const canRenderSelectedHistoryForScope = (
  selectedHistoryId: number | null,
  histories: History[] | undefined,
  allowOutOfPageSelection = false
) =>
  selectedHistoryId !== null &&
  histories !== undefined &&
  (allowOutOfPageSelection ||
    histories.some((history) => history.id === selectedHistoryId))

export const clearHistoryFilterParams = (
  searchParams: URLSearchParams,
  workspace?: Workspace
) => {
  const params = new URLSearchParams(searchParams.toString())
  params.delete("page")
  if (!workspace) {
    params.delete("wid")
  }
  RESETTABLE_FILTER_PARAM_KEYS.forEach((key) => {
    params.delete(key)
  })
  params.delete("focus")
  return params
}

export const syncSelectedHistoryParams = ({
  searchParams,
  histories,
  isMobile,
}: {
  searchParams: URLSearchParams
  histories?: History[]
  isMobile: boolean
}) => {
  if (isMobile || histories === undefined) return null

  const selectedHistoryId = getSelectedHistoryId(searchParams)
  if (
    selectedHistoryId !== null &&
    searchParams.get("focus") === HISTORY_FOCUS_PARAM_VALUE
  )
    return null

  if (histories.length === 0) {
    if (selectedHistoryId === null) return null
    const params = new URLSearchParams(searchParams.toString())
    params.delete("hid")
    return params
  }

  const hasSelectedHistory =
    selectedHistoryId !== null &&
    histories.some((history) => history.id === selectedHistoryId)

  if (hasSelectedHistory) return null

  const nextSelectedHistory = histories[0]
  if (!nextSelectedHistory) return null

  const params = new URLSearchParams(searchParams.toString())
  params.set("hid", nextSelectedHistory.id.toString())
  return params
}

export interface HistoryListProps {
  workspace?: Workspace
}

type HistoryDetailPaneErrorBoundaryState = {
  error?: unknown
}

type HistoryDetailPaneErrorBoundaryProps = React.PropsWithChildren<{
  title: string
  description: string
}>

class HistoryDetailPaneErrorBoundary extends React.Component<
  HistoryDetailPaneErrorBoundaryProps,
  HistoryDetailPaneErrorBoundaryState
> {
  state: HistoryDetailPaneErrorBoundaryState = {}

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  componentDidCatch(error: unknown) {
    console.error("History detail pane error", error)
  }

  render() {
    if (this.state.error) {
      const message =
        this.state.error instanceof Error
          ? this.state.error.message
          : String(this.state.error)
      return (
        <div className="flex h-full items-center justify-center px-5">
          <div className="max-w-sm rounded-lg border border-border bg-background p-4 text-sm">
            <div className="font-medium text-foreground">
              {this.props.title}
            </div>
            <div className="mt-1 text-muted-foreground">
              {this.props.description}
            </div>
            {import.meta.env.DEV && message && (
              <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-xs text-muted-foreground">
                {message}
              </pre>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const HistoryList = ({ workspace }: HistoryListProps = {}) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const { isMobile, width: viewportWidth } = useWindowSize()
  const [workspaces, currentOrganization, userMe] = useStore((state) => [
    state.workspaces,
    state.currentOrganization,
    state.userMe,
  ])
  const [searchParams, setSearchParams] = useSearchParams()
  const setSearchParamsRef = useRef(setSearchParams)
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams
  }, [setSearchParams])
  const externalUserId = searchParams.get("pem") || ""
  const clientUserId = searchParams.get("cuid") || ""
  const customerSearch = searchParams.get("customer")?.trim() || ""
  const page = Number.parseInt(searchParams.get("page") || "1", 10)
  const workspaceFilterId = workspace?.id || searchParams.get("wid") || ""
  const shouldDeferWorkspaceFetch = shouldDeferWorkspaceScopedHistoryFetch(
    searchParams,
    workspace
  )
  const agentFilterId = searchParams.get("aid") || ""
  const orderBy =
    (searchParams.get("ob") as HistoryOrderBy) || DEFAULT_HISTORY_ORDER
  const feedbackFilter =
    (searchParams.get("fb") as FeedbackFilterValue) || DEFAULT_FEEDBACK_FILTER
  const humanModeFilter =
    (searchParams.get("hitl") as HumanModeFilterValue) ||
    DEFAULT_HUMAN_MODE_FILTER
  const rawSourceFilter = searchParams.get("src") || searchParams.get("ch")
  const sourceFilter = isHistorySourceFilterValue(rawSourceFilter)
    ? rawSourceFilter
    : DEFAULT_SOURCE_FILTER
  const joinedWorkspaces = useMemo(
    () => workspaces?.filter((workspace) => workspace.isJoined) ?? [],
    [workspaces]
  )
  // Organization-level history access can include workspaces the current user
  // has not joined directly, so the privacy decision must use the full org
  // scope rather than only the workspace-picker subset.
  const identityScopeWorkspaces = workspace ? [workspace] : workspaces
  const isEndUserIdentityMaskedForScope = isHistoryIdentityScopeMasked(
    identityScopeWorkspaces,
    workspaceFilterId
  )

  const [agents, setAgents] = useState<Agent[]>([])
  const [loadedHistories, setLoadedHistories] = useState<History[]>()
  const [loadedHistoryScopeKey, setLoadedHistoryScopeKey] = useState("")
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const previousWorkspaceIdRef = useRef<string>("")
  const historyRequestGenerationRef = useRef(0)
  const historyAbortControllerRef = useRef<AbortController | null>(null)
  const [detailPaneWidth, setDetailPaneWidth] = useState<number>(
    getInitialHistoryDetailWidth
  )
  const splitPaneRef = useRef<HTMLDivElement | null>(null)
  const historyDetailScrollRef = useRef<HTMLDivElement | null>(null)
  const [splitPaneWidth, setSplitPaneWidth] = useState<number>()

  const {
    data: historiesResp,
    error: getHistoryError,
    status: historiesStatus,
    fetchFn: getHistories,
  } = useGetHistoriesApi()

  const {
    data: agentsResp,
    error: getAgentsError,
    status: agentsStatus,
    fetchFn: getAgents,
  } = useGetAgentsApi()

  const updateSearchParams = useCallback(
    (updates: {
      page?: number
      workspaceId?: string
      agentId?: string
      externalUserId?: string
      clientUserId?: string
      customerSearch?: string
      orderBy?: HistoryOrderBy
      feedbackFilter?: FeedbackFilterValue
      humanModeFilter?: HumanModeFilterValue
      sourceFilter?: SourceFilterValue
    }) => {
      setSearchParams((prevSearchParams) => {
        const params = new URLSearchParams(prevSearchParams.toString())
        params.delete("focus")
        if (updates.page) params.set("page", updates.page.toString())
        if (updates.workspaceId !== undefined) {
          if (updates.workspaceId === AGENT_FILTER_ALL) params.delete("wid")
          else params.set("wid", updates.workspaceId)
        }
        if (updates.agentId !== undefined) params.set("aid", updates.agentId)
        if (updates.externalUserId !== undefined) {
          const trimmed = updates.externalUserId.trim()
          if (trimmed === "") params.delete("pem")
          else params.set("pem", trimmed)
        }
        if (updates.clientUserId !== undefined) {
          const trimmed = updates.clientUserId.trim()
          if (trimmed === "") params.delete("cuid")
          else params.set("cuid", trimmed)
        }
        if (updates.customerSearch !== undefined) {
          const trimmed = updates.customerSearch.trim()
          if (trimmed === "") params.delete("customer")
          else params.set("customer", trimmed)
        }
        if (updates.orderBy) params.set("ob", updates.orderBy)
        if (updates.feedbackFilter !== undefined) {
          if (updates.feedbackFilter === DEFAULT_FEEDBACK_FILTER)
            params.delete("fb")
          else params.set("fb", updates.feedbackFilter)
        }
        if (updates.humanModeFilter !== undefined) {
          if (updates.humanModeFilter === DEFAULT_HUMAN_MODE_FILTER)
            params.delete("hitl")
          else params.set("hitl", updates.humanModeFilter)
        }
        if (updates.sourceFilter !== undefined) {
          params.delete("ch")
          if (updates.sourceFilter === DEFAULT_SOURCE_FILTER) {
            params.delete("src")
          } else {
            params.set("src", updates.sourceFilter)
          }
        }
        return params
      })
    },
    [setSearchParams]
  )

  const buildSearchParams = useCallback(
    (filters: {
      page: number
      workspaceId: string
      agentId: string
      externalUserId: string
      clientUserId: string
      customerSearch: string
      orderBy: HistoryOrderBy
      feedbackFilter: FeedbackFilterValue
      humanModeFilter: HumanModeFilterValue
      sourceFilter: SourceFilterValue
    }) => {
      const params = new URLSearchParams({
        page: filters.page.toString(),
        surface: "conversation",
      })
      if (currentOrganization) {
        params.set("organization_id", currentOrganization.id)
      }
      if (filters.workspaceId) {
        params.append("workspace_id", filters.workspaceId)
      }
      if (filters.agentId && filters.agentId !== AGENT_FILTER_NONE) {
        params.append("agent_id", filters.agentId)
      }
      if (filters.clientUserId) {
        params.append("client_user_id", filters.clientUserId)
      } else if (filters.externalUserId === "all") {
        params.append("external_user_id", filters.externalUserId)
      } else if (filters.externalUserId && !isEndUserIdentityMaskedForScope) {
        params.append("external_user_id", filters.externalUserId)
      }
      if (filters.customerSearch && !isEndUserIdentityMaskedForScope) {
        params.set("customer_search", filters.customerSearch)
      }
      if (filters.orderBy) {
        params.append("order_by", filters.orderBy)
      }
      if (filters.feedbackFilter && filters.feedbackFilter !== "no_filter") {
        params.append("feedback_filter", filters.feedbackFilter)
      }
      if (
        filters.humanModeFilter &&
        filters.humanModeFilter !== DEFAULT_HUMAN_MODE_FILTER
      ) {
        params.append("human_mode", filters.humanModeFilter)
      }
      if (CHANNEL_SOURCE_FILTER_SET.has(filters.sourceFilter)) {
        params.append("channel_type", filters.sourceFilter)
      } else if (filters.sourceFilter !== DEFAULT_SOURCE_FILTER) {
        params.append("source_type", filters.sourceFilter)
      }
      return params
    },
    [currentOrganization, isEndUserIdentityMaskedForScope]
  )

  const historyRequestSearchParams = useMemo(
    () =>
      buildSearchParams({
        page,
        workspaceId: workspaceFilterId,
        agentId: agentFilterId,
        externalUserId,
        clientUserId,
        customerSearch,
        orderBy,
        feedbackFilter,
        humanModeFilter,
        sourceFilter,
      }),
    [
      buildSearchParams,
      page,
      workspaceFilterId,
      agentFilterId,
      externalUserId,
      clientUserId,
      customerSearch,
      orderBy,
      feedbackFilter,
      humanModeFilter,
      sourceFilter,
    ]
  )
  const historyScopeKey = buildHistoryResponseScopeKey(
    historyRequestSearchParams,
    isEndUserIdentityMaskedForScope
  )
  const historyScopeKeyRef = useRef(historyScopeKey)
  historyScopeKeyRef.current = historyScopeKey
  const histories = selectHistoriesForScope(
    loadedHistoryScopeKey,
    historyScopeKey,
    loadedHistories
  )
  const scopedTotalPages =
    loadedHistoryScopeKey === historyScopeKey ? totalPages : 1
  const scopedTotalRecords =
    loadedHistoryScopeKey === historyScopeKey ? totalRecords : 0

  const requestHistories = useCallback(() => {
    historyAbortControllerRef.current?.abort()
    const abortController = new AbortController()
    historyAbortControllerRef.current = abortController
    const requestGeneration = ++historyRequestGenerationRef.current
    const responseScopeKey = historyScopeKey

    void getHistories({
      searchParams: new URLSearchParams(historyRequestSearchParams),
      signal: abortController.signal,
      callbackFn: (response) => {
        if (!response?.data) return
        if (
          !shouldApplyHistoryResponse(
            requestGeneration,
            historyRequestGenerationRef.current,
            responseScopeKey,
            historyScopeKeyRef.current
          )
        ) {
          return
        }

        const effectivePage = response.pagination?.current_page
        const requestedPage = Number.parseInt(
          historyRequestSearchParams.get("page") || "1",
          10
        )
        if (effectivePage && effectivePage !== requestedPage) {
          setSearchParamsRef.current(
            (previousParams) => {
              const params = new URLSearchParams(previousParams.toString())
              params.set("page", effectivePage.toString())
              return params
            },
            { replace: true }
          )
          return
        }

        setLoadedHistories(
          response.data.map((history) => historyResponseToHistory(history))
        )
        setTotalPages(response.pagination?.total_pages || 1)
        setTotalRecords(response.pagination?.total_records || 0)
        setLoadedHistoryScopeKey(responseScopeKey)
      },
    }).catch(() => undefined)

    return () => {
      abortController.abort()
      if (historyAbortControllerRef.current === abortController) {
        historyAbortControllerRef.current = null
      }
    }
  }, [getHistories, historyRequestSearchParams, historyScopeKey])

  useEffect(() => {
    if (!isEndUserIdentityMaskedForScope) return
    if ((!externalUserId || externalUserId === "all") && !customerSearch) return
    setSearchParams(
      (previousParams) =>
        sanitizeMaskedHistoryIdentityParams(previousParams, true) ??
        previousParams
    )
  }, [
    isEndUserIdentityMaskedForScope,
    externalUserId,
    customerSearch,
    setSearchParams,
  ])

  useEffect(() => {
    if (rawSourceFilter === null || isHistorySourceFilterValue(rawSourceFilter))
      return
    setSearchParams((previousParams) => {
      const params = new URLSearchParams(previousParams.toString())
      params.set("page", "1")
      params.delete("src")
      params.delete("ch")
      return params
    })
  }, [rawSourceFilter, setSearchParams])

  useEffect(() => {
    if (shouldDeferWorkspaceFetch) {
      historyRequestGenerationRef.current += 1
      historyAbortControllerRef.current?.abort()
      historyAbortControllerRef.current = null
      return
    }
    return requestHistories()
  }, [requestHistories, shouldDeferWorkspaceFetch])

  useEffect(
    () => () => {
      historyRequestGenerationRef.current += 1
      historyAbortControllerRef.current?.abort()
      historyAbortControllerRef.current = null
    },
    []
  )

  useEffect(() => {
    if (workspace) {
      const params = syncWorkspaceScopedHistoryParams(
        searchParams,
        workspace.id
      )
      if (!params) return
      setSearchParams(params, { replace: true })
    }
  }, [workspace, searchParams, setSearchParams])

  useEffect(() => {
    if (!userMe?.profile?.defaultOrganizationId) return
    const params: Record<string, string> = {
      oid: userMe.profile.defaultOrganizationId,
    }
    if (workspace) {
      params.wid = workspace.id
    }
    getAgents({
      searchParams: new URLSearchParams(params),
    })
  }, [getAgents, userMe?.profile?.defaultOrganizationId, workspace])

  useEffect(() => {
    if (agentsStatus === FetchStatus.Success && agentsResp?.data) {
      setAgents(agentsResp.data.map((agent) => agentResponseToAgent(agent)))
    } else if (agentsStatus === FetchStatus.Error) {
      setAgents([])
    }
  }, [agentsResp, agentsStatus])

  useEffect(() => {
    if (agentsStatus === FetchStatus.Error) {
      console.error("Failed to refresh agents", getAgentsError)

      let errorMessage = "Unknown error occurred while loading agents"
      let title = "Failed to load agents"

      if (getAgentsError) {
        if (getAgentsError instanceof HTTPError) {
          const status = getAgentsError.response.status

          switch (status) {
            case 401:
              title = "Authentication Error"
              errorMessage = "Please log in again to access agents"
              break
            case 403:
              title = "Access Denied"
              errorMessage = "You don't have permission to view agents"
              break
            case 500:
              title = "Server Error"
              errorMessage =
                "Server error while loading agents. Please try again later."
              break
            case 404:
              title = "Agents Not Found"
              errorMessage = "No agents found for this workspace"
              break
            default:
              errorMessage =
                agentsResp?.message || `Request failed with status ${status}`
          }
        } else if (getAgentsError instanceof TimeoutError) {
          title = "Request Timeout"
          errorMessage =
            "Loading agents took too long. Please check your connection and try again."
        } else if (
          getAgentsError instanceof Error &&
          getAgentsError.name === "AbortError"
        ) {
          // Don't show error for aborted requests
          return
        } else {
          errorMessage =
            agentsResp?.message || "Network error while loading agents"
        }
      }

      alertToast({
        title,
        description: errorMessage,
      })
    }
  }, [agentsStatus, getAgentsError, agentsResp, alertToast, t])

  useEffect(() => {
    // Wait for agents to load before deciding whether to drop the agent filter,
    // otherwise an in-flight fetch can race against the workspace change.
    if (agentsStatus !== FetchStatus.Success || agents.length === 0) {
      return
    }

    const currentWorkspaceId =
      workspaceFilterId ||
      userMe?.profile?.defaultWorkspaceId ||
      AGENT_FILTER_ALL
    const currentAgentId = agentFilterId || AGENT_FILTER_ALL
    const previousWorkspaceId = previousWorkspaceIdRef.current
    previousWorkspaceIdRef.current = currentWorkspaceId

    if (currentWorkspaceId === previousWorkspaceId) return
    if (previousWorkspaceId === "") return
    if (currentAgentId === AGENT_FILTER_ALL) return

    const currentAgent = agents.find((agent) => agent.id === currentAgentId)
    if (
      currentAgent &&
      currentWorkspaceId !== AGENT_FILTER_ALL &&
      currentAgent.workspaceId !== currentWorkspaceId
    ) {
      setSearchParams((prevSearchParams) => {
        const params = new URLSearchParams(prevSearchParams.toString())
        params.delete("aid")
        return params
      })
    }
  }, [
    workspaceFilterId,
    agentFilterId,
    userMe?.profile?.defaultWorkspaceId,
    agents,
    agentsStatus,
    setSearchParams,
  ])

  useEffect(() => {
    if (!getHistoryError) return
    console.error("Failed to refresh histories", getHistoryError)

    alertToast({
      title: t("historyList.message.refreshFailed"),
      description: getUserFacingApiErrorMessage(
        getHistoryError,
        t,
        historiesResp?.message || t("historyList.message.refreshFailed")
      ),
    })
  }, [getHistoryError, historiesResp, alertToast, t])

  const refetchHistories = useCallback(() => {
    requestHistories()
  }, [requestHistories])

  const handleWorkspaceChange = useCallback(
    (workspaceId: string) =>
      setSearchParams((searchParams) =>
        changeHistoryWorkspaceParams(searchParams, workspaceId)
      ),
    [setSearchParams]
  )

  const handleAgentChange = useCallback(
    (agentId: string) => updateSearchParams({ page: 1, agentId }),
    [updateSearchParams]
  )

  const handleExternalUserIdChange = useCallback(
    (externalUserId: string, nextClientUserId?: string) =>
      updateSearchParams({
        page: 1,
        externalUserId,
        clientUserId: nextClientUserId || "",
      }),
    [updateSearchParams]
  )

  const handleSortChange = useCallback(
    (orderBy: HistoryOrderBy) => updateSearchParams({ page: 1, orderBy }),
    [updateSearchParams]
  )

  const handleFeedbackChange = useCallback(
    (feedbackFilter: FeedbackFilterValue) =>
      updateSearchParams({ page: 1, feedbackFilter }),
    [updateSearchParams]
  )

  const handleHumanModeChange = useCallback(
    (humanModeFilter: HumanModeFilterValue) =>
      updateSearchParams({ page: 1, humanModeFilter }),
    [updateSearchParams]
  )

  const handleSourceChange = useCallback(
    (nextSourceFilter: SourceFilterValue) =>
      updateSearchParams({ page: 1, sourceFilter: nextSourceFilter }),
    [updateSearchParams]
  )

  const handleAgentRetry = useCallback(() => {
    if (!userMe?.profile?.defaultOrganizationId) return
    const params: Record<string, string> = {
      oid: userMe.profile.defaultOrganizationId,
    }
    if (workspace) {
      params.wid = workspace.id
    }
    getAgents({
      searchParams: new URLSearchParams(params),
    })
  }, [getAgents, userMe?.profile?.defaultOrganizationId, workspace])

  const lockedWorkspaceId = workspaceFilterId
  const hasActiveFilters = hasActiveHistoryFilters(searchParams, workspace)
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (!!searchParams.get("wid") && !workspace) count += 1
    const aid = searchParams.get("aid")
    if (aid && aid !== AGENT_FILTER_ALL && aid !== AGENT_FILTER_NONE) count += 1
    if (searchParams.get("pem") || searchParams.get("cuid")) count += 1
    if (searchParams.get("customer")?.trim()) count += 1
    const feedback = searchParams.get("fb")
    if (feedback && feedback !== DEFAULT_FEEDBACK_FILTER) count += 1
    const humanMode = searchParams.get("hitl")
    if (humanMode && humanMode !== DEFAULT_HUMAN_MODE_FILTER) count += 1
    const source = searchParams.get("src") || searchParams.get("ch")
    if (source && source !== DEFAULT_SOURCE_FILTER) count += 1
    if (searchParams.get("ob") === "asc") count += 1
    return count
  }, [searchParams, workspace])
  const selectedHistoryId = getSelectedHistoryId(searchParams)
  const canRenderSelectedHistory = canRenderSelectedHistoryForScope(
    selectedHistoryId,
    histories,
    searchParams.get("focus") === HISTORY_FOCUS_PARAM_VALUE
  )
  const hasPagedHistories =
    historiesStatus === FetchStatus.Success &&
    histories !== undefined &&
    histories.length > 0
  const availableDetailPaneWidth =
    (splitPaneWidth ?? viewportWidth) -
    HISTORY_LIST_MIN_WIDTH -
    SPLIT_PANE_BORDER_PX
  const minDetailPaneWidth = Math.min(
    HISTORY_DETAIL_MIN_WIDTH,
    Math.max(HISTORY_DETAIL_COMPACT_MIN_WIDTH, availableDetailPaneWidth)
  )
  const maxDetailPaneWidth = useMemo(
    () =>
      Math.max(
        minDetailPaneWidth,
        Math.min(HISTORY_DETAIL_MAX_WIDTH, availableDetailPaneWidth)
      ),
    [availableDetailPaneWidth, minDetailPaneWidth]
  )
  const effectiveDetailPaneWidth = clampHistoryDetailWidth(
    detailPaneWidth,
    maxDetailPaneWidth,
    minDetailPaneWidth
  )
  const { isResizing, onResizeStart, onResizeKeyDown } = useDrawerResize({
    width: effectiveDetailPaneWidth,
    onWidthChange: setDetailPaneWidth,
    enabled: !isMobile,
    minWidth: minDetailPaneWidth,
    maxWidth: maxDetailPaneWidth,
  })

  const handleClearFilters = useCallback(() => {
    setSearchParams((prevSearchParams) => {
      return clearHistoryFilterParams(prevSearchParams, workspace)
    })
  }, [setSearchParams, workspace])

  const handleHistorySelect = useCallback(
    (history: History) => {
      setSearchParams((prevSearchParams) => {
        const params = new URLSearchParams(prevSearchParams.toString())
        params.delete("focus")
        params.set("hid", history.id.toString())
        return params
      })
    },
    [setSearchParams]
  )

  const handleHistoryDelete = useCallback(
    (history: History) => {
      if (selectedHistoryId === history.id) {
        setLoadedHistories(undefined)
        setSearchParams((prevSearchParams) => {
          const params = new URLSearchParams(prevSearchParams.toString())
          params.delete("hid")
          params.delete("focus")
          return params
        })
      }
      refetchHistories()
    },
    [refetchHistories, selectedHistoryId, setSearchParams]
  )

  useEffect(() => {
    const nextParams = syncSelectedHistoryParams({
      searchParams,
      histories,
      isMobile,
    })
    if (!nextParams) return

    setSearchParams(nextParams, { replace: true })
  }, [histories, isMobile, searchParams, setSearchParams])

  useEffect(() => {
    if (isMobile || selectedHistoryId === null) return

    historyDetailScrollRef.current?.scrollTo({ top: 0 })
  }, [isMobile, selectedHistoryId])

  useEffect(() => {
    if (isMobile) return

    setDetailPaneWidth((prevWidth) =>
      clampHistoryDetailWidth(prevWidth, maxDetailPaneWidth, minDetailPaneWidth)
    )
  }, [isMobile, maxDetailPaneWidth, minDetailPaneWidth])

  useEffect(() => {
    if (isMobile) return
    const splitPane = splitPaneRef.current
    if (!splitPane) return

    const updateSplitPaneWidth = () => {
      const next = splitPane.clientWidth
      // Skip no-op updates so a window-resize storm doesn't re-render every frame.
      setSplitPaneWidth((prev) => (prev === next ? prev : next))
    }
    updateSplitPaneWidth()

    const resizeObserver = new ResizeObserver(updateSplitPaneWidth)
    resizeObserver.observe(splitPane)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isMobile])

  const lastPersistedWidthRef = useRef<number | null>(null)
  useEffect(() => {
    if (typeof window === "undefined" || isMobile || isResizing) return
    if (lastPersistedWidthRef.current === effectiveDetailPaneWidth) return

    lastPersistedWidthRef.current = effectiveDetailPaneWidth
    window.localStorage.setItem(
      HISTORY_DETAIL_WIDTH_STORAGE_KEY,
      effectiveDetailPaneWidth.toString()
    )
  }, [effectiveDetailPaneWidth, isMobile, isResizing])

  if (
    shouldShowHistoryOnboarding({
      historiesStatus,
      histories,
      hasActiveFilters,
    })
  ) {
    return (
      <OnboardingEmptyState
        icon={MessageSquare}
        title={t("historyList.onboarding.title")}
        description={t("historyList.onboarding.description")}
      />
    )
  }

  const historyListBody =
    historiesStatus === FetchStatus.Error ? (
      <div className="px-4 py-6">
        <OnboardingEmptyState
          icon={AlertCircle}
          title={t("historyList.error.title")}
          description={t("historyList.error.description")}
          className="py-16"
        />
      </div>
    ) : historiesStatus === FetchStatus.Loading || histories === undefined ? (
      <div className="px-4 py-6">
        <LoadingPage message={t(DEFAULT_LOADING_MESSAGE)} topOffset="16px" />
      </div>
    ) : histories.length === 0 ? (
      <div className="px-4 py-6">
        <OnboardingEmptyState
          icon={MessageSquare}
          title={t("historyList.filteredEmpty.title")}
          description={t("historyList.filteredEmpty.description")}
          secondaryAction={{
            label: t("historyList.filteredEmpty.clearFilters"),
            onClick: handleClearFilters,
          }}
          className="py-16"
        />
      </div>
    ) : (
      <div className="divide-y divide-border/50">
        {histories.map((history) => (
          <HistoryCard
            key={history.id}
            history={history}
            selected={!isMobile && selectedHistoryId === history.id}
            showWorkspaceBadge={!workspace}
            onSelect={!isMobile ? handleHistorySelect : undefined}
            onUpdate={refetchHistories}
            onDelete={handleHistoryDelete}
          />
        ))}
      </div>
    )

  const listHeader = (
    <div className="shrink-0 border-b bg-background px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="min-w-0 truncate text-base font-semibold leading-snug tracking-tight text-foreground">
          {t("historyList.title", { count: scopedTotalRecords })}
        </h3>
        <div className="flex shrink-0 items-center gap-1.5">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 gap-1.5 px-2.5 text-xs font-medium",
                  activeFilterCount > 0 &&
                    "border-primary/30 bg-primary/5 text-foreground hover:bg-primary/10"
                )}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span>{t("historyList.filters.label")}</span>
                {activeFilterCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 min-w-4 justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-80 space-y-3 p-3"
              sideOffset={6}
            >
              {lockedWorkspaceId === "" && (
                <WorkspaceSelect
                  workspaces={joinedWorkspaces}
                  onWorkspaceChange={handleWorkspaceChange}
                  defaultValue={userMe?.profile?.defaultWorkspaceId || "all"}
                />
              )}
              <AgentSelect
                agents={agents}
                currentWorkspaceId={
                  lockedWorkspaceId || userMe?.profile?.defaultWorkspaceId || ""
                }
                onAgentChange={handleAgentChange}
                defaultValue={searchParams.get("aid") || "all"}
                compact={true}
                isLoading={agentsStatus === FetchStatus.Loading}
                hasError={agentsStatus === FetchStatus.Error}
                onRetry={handleAgentRetry}
              />
              <ExternalUserIdSelect
                defaultValue={
                  isEndUserIdentityMaskedForScope ? "" : externalUserId
                }
                defaultClientUserId={clientUserId}
                onChange={handleExternalUserIdChange}
                compact={true}
                disableFreeTextSelection={isEndUserIdentityMaskedForScope}
                currentWorkspaceId={lockedWorkspaceId}
                currentOrganizationId={currentOrganization?.id}
              />
              <SourceSelect
                value={sourceFilter}
                compact={true}
                onSourceChange={handleSourceChange}
              />
              <HumanModeSelect
                value={humanModeFilter}
                compact={true}
                onHumanModeChange={handleHumanModeChange}
              />
              <FeedbackSelect
                defaultValue={feedbackFilter}
                compact={true}
                onFeedbackChange={handleFeedbackChange}
              />
              {activeFilterCount > 0 && (
                <div className="flex justify-end border-t border-border/60 pt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="h-8 text-xs font-medium"
                  >
                    {t("historyList.filteredEmpty.clearFilters")}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <SortToggle
            value={orderBy}
            iconOnly={true}
            onChange={handleSortChange}
          />
        </div>
      </div>
      <CustomerSearch
        value={customerSearch}
        identityMasked={isEndUserIdentityMaskedForScope}
        onSearch={(query) =>
          updateSearchParams({ page: 1, customerSearch: query })
        }
      />
    </div>
  )

  const renderPagination = (variant: "mobile" | "desktop") => {
    if (!hasPagedHistories) return null

    return (
      <div
        className={cn(
          "border-t bg-background px-4 py-3",
          variant === "desktop"
            ? "absolute inset-x-0 bottom-0 z-10 shadow-[0_-8px_18px_-16px_hsl(var(--foreground)/0.08)]"
            : "shrink-0"
        )}
      >
        <PageNav
          className="mb-0"
          page={parseInt(searchParams.get("page") || "1")}
          totalPages={scopedTotalPages}
          onPageChange={(page) => updateSearchParams({ page })}
        />
      </div>
    )
  }

  const listPaneDesktopContent = historyListBody

  if (isMobile) {
    return (
      <div className="rounded-xl border bg-background">
        {listHeader}
        <div className="overflow-x-hidden">{historyListBody}</div>
        {renderPagination("mobile")}
      </div>
    )
  }

  return (
    <div
      ref={splitPaneRef}
      className="flex h-full min-h-0 overflow-hidden rounded-xl border bg-background"
    >
      <section
        className="relative h-full min-h-0 min-w-0 flex-1 overflow-hidden border-r bg-background"
        style={{ minWidth: `${HISTORY_LIST_MIN_WIDTH}px` }}
      >
        <div className="flex h-full min-h-0 flex-col">
          {listHeader}
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain",
              hasPagedHistories && "pb-20"
            )}
          >
            {listPaneDesktopContent}
          </div>
        </div>
        {renderPagination("desktop")}
      </section>

      <section
        className={cn(
          "relative h-full min-h-0 shrink-0 overflow-hidden bg-background",
          isResizing && "select-none"
        )}
        style={{ width: `${effectiveDetailPaneWidth}px` }}
      >
        <ResizeHandle
          ariaLabel={t("historyList.detail.resizeHandle")}
          width={effectiveDetailPaneWidth}
          minWidth={minDetailPaneWidth}
          maxWidth={maxDetailPaneWidth}
          isResizing={isResizing}
          onResizeStart={onResizeStart}
          onKeyDown={onResizeKeyDown}
        />
        <div
          ref={historyDetailScrollRef}
          className="h-full min-h-0 overflow-y-auto overscroll-contain"
          data-scroll-container={HISTORY_DETAIL_SCROLL_CONTAINER}
        >
          {canRenderSelectedHistory && selectedHistoryId !== null ? (
            <HistoryDetailPaneErrorBoundary
              key={selectedHistoryId}
              title={t("historyList.detail.error.title")}
              description={t("historyList.detail.error.description")}
            >
              <HistoryDetail
                historyId={selectedHistoryId}
                mode="embedded"
                onPlatformReplySent={refetchHistories}
              />
            </HistoryDetailPaneErrorBoundary>
          ) : (
            <div className="flex h-full items-center justify-center px-5 text-sm text-muted-foreground">
              {t("historyList.filteredEmpty.description")}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default HistoryList
